// ✅ src/pages/OrderManagement/ProjectListing.jsx  (FULL FILE UPDATED)
// Backend mount:
//   app.use("/api/projectListing", projectListing);
//
// Endpoints used (you implement in Node):
//   GET  https://nissi-india.com/api/projectListing/worklocations
//   POST https://nissi-india.com/api/projectListing/search
//   POST https://nissi-india.com/api/projectListing/viewAll   (optional, can call /search with empty filters)

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProjectPostOrderLayoutWrapper from "../../components/ProjectPostOrderLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSearch, FaEye, FaChevronUp, FaChevronDown } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/projectListing";

const OngoingProjectListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchLS = getLS("branchType") || getLS("branchtype");
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ parsed values
  const branchId = useMemo(() => Number(branchIdLS || 0), [branchIdLS]);
  const branchType = useMemo(() => String(branchLS || "").trim(), [branchLS]);
  const userId = useMemo(() => String(userIdLS || "").trim(), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // Hide/Show filters state
  const [showFilters, setShowFilters] = useState(true);

  // Dropdown work locations
  const [workLocations, setWorkLocations] = useState([
    { value: "Select", label: "Select" },
  ]);

  // Loading/error
  const [loading, setLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    fromDate: "",
    toDate: "",
    srcJdNo: "",
    clientName: "",
    contractName: "",
    location: "Select",
  });

  // Grid data
  const [rows, setRows] = useState([]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ✅ Load Work Location dropdown
  const loadWorkLocations = async () => {
    try {
      const res = await axios.get(`${API_BASE}/worklocations`, {
        headers: commonHeaders,
      });

      const list = res.data?.data || [];
      if (Array.isArray(list) && list.length) {
        setWorkLocations(list);
      } else {
        setWorkLocations([{ value: "Select", label: "Select" }]);
      }
    } catch (err) {
      setWorkLocations([{ value: "Select", label: "Select" }]);
      toast.error("Failed to load work locations");
    }
  };

  // ✅ Search
  const handleSearch = async () => {
    setLoading(true);
    try {
      const payload = {
        ...formData,
        branchId,
        branchType,
        userId,
        finYearId,
        finYear,
      };

      const res = await axios.post(`${API_BASE}/search`, payload, {
        headers: commonHeaders,
      });

      const data = res.data?.data || [];
      setRows(Array.isArray(data) ? data : []);

      if (!data?.length) toast.info("No records found");
    } catch (err) {
      setRows([]);
      toast.error(err?.response?.data?.message || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  // ✅ View All
  const handleViewAll = async () => {
    const emptyForm = {
      fromDate: "",
      toDate: "",
      srcJdNo: "",
      clientName: "",
      contractName: "",
      location: "Select",
    };

    setFormData(emptyForm);
    setLoading(true);

    try {
      const res = await axios.post(
        `${API_BASE}/viewAll`,
        {
          branchId,
          branchType,
          userId,
          finYearId,
          finYear,
        },
        {
          headers: commonHeaders,
        }
      );

      const data = res.data?.data || [];
      setRows(Array.isArray(data) ? data : []);

      if (!data?.length) toast.info("No records found");
    } catch (err) {
      try {
        const res2 = await axios.post(
          `${API_BASE}/search`,
          {
            ...emptyForm,
            branchId,
            branchType,
            userId,
            finYearId,
            finYear,
          },
          {
            headers: commonHeaders,
          }
        );

        const data2 = res2.data?.data || [];
        setRows(Array.isArray(data2) ? data2 : []);

        if (!data2?.length) toast.info("No records found");
      } catch (err2) {
        setRows([]);
        toast.error(err2?.response?.data?.message || "View All failed");
      }
    } finally {
      setLoading(false);
    }
  };

  // ✅ click contract name -> open entry
  const openEntry = (contractId) => {
    if (!contractId) {
      toast.error("ContractId missing");
      return;
    }
    navigate(`/OrderManagement/ProjectEntry?contractId=${contractId}`);
  };

  useEffect(() => {
    loadWorkLocations();
    handleViewAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ProjectPostOrderLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ONGOING PROJECT LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              OnGoing Project Listing
            </h1>

            <button
              onClick={() => setShowFilters((p) => !p)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
              }`}
            >
              {showFilters ? <FaChevronUp /> : <FaChevronDown />}
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          </div>

          {showFilters && (
            <div
              className={`p-6 rounded-xl border mb-6 ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    FromDate
                  </label>
                  <input
                    type="date"
                    value={formData.fromDate}
                    onChange={(e) => handleInputChange("fromDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    SRC_ID No
                  </label>
                  <input
                    type="text"
                    value={formData.srcJdNo}
                    onChange={(e) => handleInputChange("srcJdNo", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Contract Name
                  </label>
                  <input
                    type="text"
                    value={formData.contractName}
                    onChange={(e) => handleInputChange("contractName", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    ToDate
                  </label>
                  <input
                    type="date"
                    value={formData.toDate}
                    onChange={(e) => handleInputChange("toDate", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Client Name
                  </label>
                  <input
                    type="text"
                    value={formData.clientName}
                    onChange={(e) => handleInputChange("clientName", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Location
                  </label>
                  <select
                    value={formData.location}
                    onChange={(e) => handleInputChange("location", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    {workLocations.map((w) => (
                      <option key={w.value} value={w.value}>
                        {w.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-center items-center mt-6 gap-4">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    isDark ? `${buttonClass} ${hoverClass} text-white` : `${buttonClass} ${hoverClass} text-white`
                  } ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <FaSearch />
                  {loading ? "Searching..." : "Search"}
                </button>

                <button
                  onClick={handleViewAll}
                  disabled={loading}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    isDark ? `${buttonClass} ${hoverClass} text-white` : `${buttonClass} ${hoverClass} text-white`
                  } ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <FaEye />
                  View All
                </button>
              </div>
            </div>
          )}

          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-3 py-4 text-center font-semibold border-r border-white/20">Slno</th>
                    <th className="px-3 py-4 text-center font-semibold border-r border-white/20">Contract Name</th>
                    <th className="px-3 py-4 text-center font-semibold border-r border-white/20" colSpan="3">
                      <div className="text-center mb-1">Value</div>
                      <div className="grid grid-cols-3 text-xs">
                        <div className="border-r border-white/20 px-2">Contract</div>
                        <div className="border-r border-white/20 px-2">Operative</div>
                        <div className="px-2">Workdone as on date</div>
                      </div>
                    </th>
                    <th className="px-3 py-4 text-center font-semibold border-r border-white/20">
                      Completion Date as per Contract
                    </th>
                    <th className="px-3 py-4 text-center font-semibold">
                      Antisip date of completion date
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className={`px-3 py-6 text-center ${isDark ? "text-gray-200" : "text-gray-600"}`}
                      >
                        No records
                      </td>
                    </tr>
                  ) : (
                    rows.map((item, idx) => (
                      <tr
                        key={item.contractId ? `${item.contractId}` : `row-${idx}`}
                        className={`transition-colors duration-200 ${
                          isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"
                        }`}
                      >
                        <td
                          className={`px-3 py-3 text-center border-r ${
                            isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.slno ?? idx + 1}
                        </td>

                        <td
                          className={`px-3 py-3 text-center border-r ${
                            isDark ? "border-gray-700 text-blue-400" : "border-gray-200 text-blue-600"
                          }`}
                        >
                          <span
                            onClick={() => openEntry(item.contractId)}
                            className="hover:underline cursor-pointer"
                            title="Open Project Entry"
                          >
                            {item.contractName}
                          </span>
                        </td>

                        <td
                          className={`px-3 py-3 text-center border-r ${
                            isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.contract}
                        </td>

                        <td
                          className={`px-3 py-3 text-center border-r ${
                            isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.operative}
                        </td>

                        <td
                          className={`px-3 py-3 text-center border-r ${
                            isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.workdoneAsOnDate}
                        </td>

                        <td
                          className={`px-3 py-3 text-center border-r ${
                            isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"
                          }`}
                        >
                          {item.completionDate}
                        </td>

                        <td className={`px-3 py-3 text-center ${isDark ? "text-white" : "text-gray-900"}`}>
                          {item.anticipatedCompletionDate}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProjectPostOrderLayoutWrapper>
  );
};

export default OngoingProjectListing;